import http from '@/lib/http'
import { useQuery } from '@tanstack/react-query'

/**
 * Shape of a single patient returned by the linked patients endpoint.
 * Each patient in the group shares the same `linked_patient_hash`.
 */
export type LinkedPatient = {
  hash: string
  first_name: string
  last_name: string
  date_of_birth: string | null
  gender: string | null
  postal_code: string | null
  allergies: string
  conditions: string
  medications: string
  family_history: string
  social_history: string
  other_observations: string
  safeguarding_questions: Record<string, string> | null
}

type LinkedPatientsResponse = {
  /**
   * The shared group hash used for the lookup.
   * This is NOT a patient.hash — it is the patient.linked_patient_hash value.
   */
  linked_patient_hash: string
  patients: LinkedPatient[]
}

/**
 * Fetches all patients that share the given `linkedPatientHash`.
 *
 * @param linkedPatientHash — the shared group identifier (patient.linked_patient_hash, e.g. "patlnk_0dac..."),
 *                            NOT a regular patient hash.
 */
export const useLinkedPatients = (linkedPatientHash: string | null | undefined) => {
  return useQuery<LinkedPatientsResponse>({
    queryKey: ['linkedPatients', linkedPatientHash],
    queryFn: () => http.get(`/patients/linked/${linkedPatientHash}`),
    enabled: !!linkedPatientHash,
    staleTime: 5 * 60 * 1000
  })
}
